import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { mergePriceObjects } from 'pricing-pages-lib/utils/pricingUtils';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { ANNUAL } from 'self-service-api/constants/Terms';
import styled from 'styled-components';
import { PlanBuilderContext } from '../../../context/PlanBuilderContext';
import { useAppSelector } from '../../../reducers';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { priceBreakdownSelector } from '../../../selectors/PricingSelectors';
import { hasPaidHubInProductInventorySelector, productsFromProductInventoryQuantitySelector } from '../../../selectors/ProductInventorySelectors';
import { crmSuiteProductLevelSelector, isCoreSeatSkuSelector, isPaidCoreSeatSkuSelector, productVersionSelector, skuIdIsAddonOrServiceSelector, skuIdIsHubSelector, skuIdIsPartOfCrmSuiteSelector, skuIdIsQuantityPackSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import PriceDisplay from '../../common/pricing/PriceDisplay';
import CoreSeatsPicker from '../CoreSeatsPicker';
import { Divider } from '../SharedComponents';
import ShoppingCartItem, { ShoppingCartItemContainer } from './ShoppingCartItem';
const SuiteHeaderWrapper = styled.div.withConfig({
  displayName: "ShoppingCart__SuiteHeaderWrapper",
  componentId: "sc-1utirp7-0"
})(["font-weight:600;margin-bottom:10px;"]);
const Bold = styled.span.withConfig({
  displayName: "ShoppingCart__Bold",
  componentId: "sc-1utirp7-1"
})(["font-weight:600;"]);
const HeaderContainer = styled.div.withConfig({
  displayName: "ShoppingCart__HeaderContainer",
  componentId: "sc-1utirp7-2"
})(["display:flex;margin-bottom:10px;font-weight:500;font-size:14px;"]);

const shouldIncludeLineItemSelector = (state, {
  productConfigurations,
  skuId,
  quantity,
  isUngatedForSeats
}) => {
  const sku = skuSelector(state, {
    skuId
  });
  const ownedQuantity = productsFromProductInventoryQuantitySelector(state, {
    skuId
  });
  const isAddonOrService = skuIdIsAddonOrServiceSelector(state, {
    skuId
  });
  const {
    quantifiable
  } = productVersionSelector(state, {
    skuId
  });
  const isAddingZeroUnits = (isAddonOrService && quantifiable || sku.skuType === QUANTITY_PACK) && ownedQuantity === quantity;
  const shouldLumpIntoHubLineItem = sku.skuType === QUANTITY_PACK && productConfigurations.some(({
    skuId: searchSkuId
  }) => searchSkuId === sku.productVersionSkuId) && !isUngatedForSeats;
  return !(isAddingZeroUnits || shouldLumpIntoHubLineItem);
};

const ShoppingCart = ({
  productConfigurations,
  onRemove,
  className,
  requiredSkuIds = [],
  queryParamTerm,
  allowRemoveCartItems = true
}) => {
  const {
    setProductConfigurations
  } = useContext(PlanBuilderContext);
  const suiteProductLevel = useAppSelector(state => crmSuiteProductLevelSelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const ownsAnyHubs = useAppSelector(state => hasPaidHubInProductInventorySelector(state, {
    productLevels: []
  }));
  const selectedAnyHubs = useAppSelector(state => {
    return productConfigurations.some(productConfiguration => skuIdIsHubSelector(state, {
      skuId: productConfiguration.skuId
    }));
  });
  const [paidCoreSeatProductConfigs, freeCoreSeatProductConfigs, suiteProductConfigs, hubProductConfigs, nonSuiteProductConfigs] = useAppSelector(state => productConfigurations.reduce((acc, config) => {
    const productVersion = productVersionSelector(state, {
      skuId: config.skuId
    });
    const isPartOfCrmSuite = skuIdIsPartOfCrmSuiteSelector(state, {
      skuId: productVersion.skuId,
      suiteProductLevel
    });
    const isCoreSeat = isCoreSeatSkuSelector(state, {
      skuId: config.skuId
    });
    const isPaidCoreSeat = isPaidCoreSeatSkuSelector(state, {
      skuId: config.skuId
    });
    const isFreeCoreSeat = isCoreSeat && !isPaidCoreSeat;
    const isHubSku = skuIdIsHubSelector(state, {
      skuId: config.skuId
    });
    const isQuantityPack = skuIdIsQuantityPackSelector(state, {
      skuId: config.skuId
    });
    if (isPaidCoreSeat) return [[...acc[0], config], acc[1], acc[2], acc[3], acc[4]];
    if (isFreeCoreSeat) return [acc[0], [...acc[1], config], acc[2], acc[3], acc[4]];
    if (isPartOfCrmSuite) return [acc[0], acc[1], [...acc[2], config], acc[3], acc[4]];
    if (isHubSku || isQuantityPack) return [acc[0], acc[1], acc[2], [...acc[3], config], acc[4]];
    return [acc[0], acc[1], acc[2], acc[3], [...acc[4], config]];
  }, [[], [], [], [], []]));
  const [filteredSuiteProductConfigs, filteredNonSuiteProductConfigs, filteredPaidCoreSeatProductConfigs, filteredFreeCoreSeatProductConfigs, filteredHubProductConfigs] = useAppSelector(state => [suiteProductConfigs.filter(config => shouldIncludeLineItemSelector(state, Object.assign({
    productConfigurations,
    isUngatedForSeats
  }, config))), nonSuiteProductConfigs.filter(config => shouldIncludeLineItemSelector(state, Object.assign({
    productConfigurations,
    isUngatedForSeats
  }, config))), paidCoreSeatProductConfigs.filter(config => shouldIncludeLineItemSelector(state, Object.assign({
    productConfigurations,
    isUngatedForSeats
  }, config))), freeCoreSeatProductConfigs.filter(config => shouldIncludeLineItemSelector(state, Object.assign({
    productConfigurations,
    isUngatedForSeats
  }, config))), hubProductConfigs.filter(config => shouldIncludeLineItemSelector(state, Object.assign({
    productConfigurations,
    isUngatedForSeats
  }, config)))]);
  const filteredProductConfigurations = useAppSelector(state => productConfigurations.filter(({
    skuId,
    quantity
  }) => shouldIncludeLineItemSelector(state, {
    skuId,
    quantity,
    productConfigurations,
    isUngatedForSeats
  })));
  const filteredSkuIds = filteredProductConfigurations.map(({
    skuId
  }) => skuId);
  const [filteredSuiteSkuIds, filteredNonSuiteSkuIds, filteredPaidCoreSeatSkuIds, filteredFreeCoreSeatSkuIds, filteredHubSkuIds] = [filteredSuiteProductConfigs.map(({
    skuId
  }) => skuId), filteredNonSuiteProductConfigs.map(({
    skuId
  }) => skuId), filteredPaidCoreSeatProductConfigs.map(({
    skuId
  }) => skuId), filteredFreeCoreSeatProductConfigs.map(({
    skuId
  }) => skuId), filteredHubProductConfigs.map(({
    skuId
  }) => skuId)];
  let content;

  const makeShoppingCartItem = (skuId, priceOverride) => /*#__PURE__*/_jsx(ShoppingCartItem, {
    queryParamTerm: queryParamTerm,
    onRemove: () => onRemove(skuId),
    skuId: skuId,
    allProductConfigurations: productConfigurations,
    isCloseable: allowRemoveCartItems && !requiredSkuIds.includes(skuId),
    shouldDisplayPrice: !(suiteProductLevel && filteredSuiteSkuIds.includes(skuId)),
    priceOverride: priceOverride
  }, skuId);

  const currencyCode = useSelector(activeCurrencySelector);
  const priceBreakdown = useAppSelector(state => {
    return priceBreakdownSelector(state, {
      productConfigurations,
      queryParamTerm
    });
  });
  const suiteItemsPrice = mergePriceObjects(...priceBreakdown.filter(({
    skuId
  }) => suiteProductConfigs.some(({
    skuId: searchSkuId
  }) => searchSkuId === skuId)));
  const nonSuiteItemsPrice = mergePriceObjects(...priceBreakdown.filter(({
    skuId
  }) => nonSuiteProductConfigs.some(({
    skuId: searchSkuId
  }) => searchSkuId === skuId)));
  const showPaidCoreSeats = isUngatedForSeats && (filteredPaidCoreSeatSkuIds || ownsAnyHubs || selectedAnyHubs);

  const PaidCoreSeats = () => /*#__PURE__*/_jsx(ShoppingCartItemContainer, {
    "data-selenium-class": "shopping-cart-item-container",
    children: /*#__PURE__*/_jsx(CoreSeatsPicker, {
      queryParamTerm: queryParamTerm,
      productConfigurations: productConfigurations,
      setProductConfigurations: setProductConfigurations,
      isShoppingCartItem: true
    })
  });

  if (suiteProductLevel) {
    const suiteItems = filteredSuiteSkuIds.map(skuId => makeShoppingCartItem(skuId));
    const nonSuiteItems = filteredNonSuiteSkuIds.map(skuId => makeShoppingCartItem(skuId, priceBreakdown.find(({
      skuId: searchSkuId
    }) => skuId === searchSkuId)));
    const freeCoreSeatItems = filteredFreeCoreSeatSkuIds.map(skuId => makeShoppingCartItem(skuId, priceBreakdown.find(({
      skuId: searchSkuId
    }) => skuId === searchSkuId)));
    content = /*#__PURE__*/_jsxs("div", {
      children: [isUngatedForSeats ? /*#__PURE__*/_jsxs("div", {
        className: "m-bottom-3",
        children: [/*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "planBuilder.shoppingCart.monthlyCostOnlySuiteCrmSuite_jsx",
          options: {
            priceDisplayProps: {
              price: suiteItemsPrice.monthly.calculatedPrice,
              currencyCode
            }
          },
          elements: {
            PriceDisplay,
            Bold
          }
        }), queryParamTerm === ANNUAL && /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "planBuilder.shoppingCart.annualCostOnlySuiteCrmSuite_jsx",
            options: {
              priceDisplayProps: {
                price: suiteItemsPrice.annually.calculatedPrice,
                currencyCode
              }
            },
            elements: {
              PriceDisplay,
              Bold
            }
          })
        })]
      }) : /*#__PURE__*/_jsx(SuiteHeaderWrapper, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "planBuilder.shoppingCart.costOnlySuiteCrmSuite_jsx",
          options: {
            priceDisplayProps: {
              price: suiteItemsPrice.monthly.calculatedPrice,
              currencyCode
            }
          },
          elements: {
            PriceDisplay
          }
        })
      }), suiteItems, freeCoreSeatItems, showPaidCoreSeats && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx(SuiteHeaderWrapper, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "planBuilder.shoppingCart.coreSeats"
          })
        }), /*#__PURE__*/_jsx(PaidCoreSeats, {})]
      }), Boolean(nonSuiteItems.length) && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx(SuiteHeaderWrapper, {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "planBuilder.shoppingCart.costAddOnsAndServices_jsx",
            options: {
              priceDisplayProps: {
                price: nonSuiteItemsPrice.monthly.calculatedPrice,
                currencyCode
              }
            },
            elements: {
              PriceDisplay
            }
          })
        }), nonSuiteItems]
      })]
    });
  } else if (isUngatedForSeats) {
    const hubItems = filteredHubSkuIds.map(skuId => makeShoppingCartItem(skuId));
    const freeCoreSeatItems = filteredFreeCoreSeatSkuIds.map(skuId => makeShoppingCartItem(skuId));
    const addOnsAndServices = filteredNonSuiteSkuIds.map(skuId => makeShoppingCartItem(skuId));
    content = /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(HeaderContainer, {
        style: {
          justifyContent: 'center'
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "planBuilder.shoppingCart.hubSelections"
        })
      }), hubItems, freeCoreSeatItems, showPaidCoreSeats && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(HeaderContainer, {
          style: {
            justifyContent: 'center'
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "planBuilder.shoppingCart.coreSeats"
          })
        }), /*#__PURE__*/_jsx(PaidCoreSeats, {})]
      }), addOnsAndServices && !!addOnsAndServices.length && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(HeaderContainer, {
          style: {
            justifyContent: 'center'
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "planBuilder.shoppingCart.addOnsAndServices"
          })
        }), addOnsAndServices]
      })]
    });
  } else {
    const items = filteredSkuIds.map(skuId => makeShoppingCartItem(skuId));
    if (items.length === 0) return null;
    content = /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(HeaderContainer, {
        style: {
          justifyContent: 'center'
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "planBuilder.shoppingCart.productSelections"
        })
      }), items]
    });
  }

  return /*#__PURE__*/_jsx("div", {
    className: className,
    children: content
  });
};

export default ShoppingCart;